import {
  DEAL_OPERATIONS,
  DEAL_PROPERTY_STATUSES,
  LEAD_CHANNELS,
  PARTICIPANT_ROLES,
  PROPERTY_TYPES,
  TIMELINE_KINDS,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPhone,
  type DealPropertyStatus,
  type ParticipantRole,
  type TimelineKind,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { AtSign, Pencil, Plus, Trash2, TriangleAlert, UserRoundCog, X } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { ActivitiesCard } from '../../components/activities/activities-card.tsx';
import { DealStatusBadge, NextActivity, StaleBadge } from '../../components/deals/badges.tsx';
import {
  InterestFields,
  interestBody,
  type InterestValue,
} from '../../components/deals/interest-fields.tsx';
import { StatusBadge } from '../../components/properties/badges.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Combobox } from '../../components/ui/combobox.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { useAssignees, useChannels, useContacts } from '../../lib/contacts-api.ts';
import {
  dealKeys,
  useDeal,
  useLostReasons,
  useMentionable,
  usePipelines,
  useSuggestions,
  useTimeline,
  type DealDetail,
  type TimelineItem,
} from '../../lib/deals-api.ts';
import { priceLabel, useProperties } from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

export function DealDetailPage() {
  const { id = '' } = useParams();
  const deal = useDeal(id);
  if (deal.isPending) return <Loading />;
  if (deal.error) {
    return (deal.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(deal.error)}</Alert>
    );
  }
  return <DealView deal={deal.data} />;
}

/** Runs a change on the deal and refreshes it (and the lists) with the answer. */
function useDealAction(id: string) {
  const client = useQueryClient();
  const notify = useToast();
  return async (
    path: string,
    body: unknown,
    options: { method?: 'POST' | 'PATCH' | 'DELETE'; done?: string } = {},
  ): Promise<boolean> => {
    try {
      const saved = await api<DealDetail>(`/deals/${id}${path}`, {
        method: options.method ?? 'POST',
        body,
      });
      client.setQueryData(dealKeys.detail(id), saved);
      await client.invalidateQueries({ queryKey: dealKeys.all });
      if (options.done) notify(options.done, 'success');
      return true;
    } catch (error) {
      notify(errorMessage(error), 'error');
      return false;
    }
  };
}

function DealView({ deal }: { deal: DealDetail }) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const pipelines = usePipelines();
  const act = useDealAction(deal.id);
  const [dialog, setDialog] = useState<'win' | 'lose' | 'reassign' | 'edit' | 'delete' | null>(
    null,
  );
  const stages = pipelines.data?.find((p) => p.id === deal.pipeline.id)?.stages ?? [];
  const close = () => {
    setDialog(null);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/negocios" className="text-sm text-primary hover:underline">
          ← Negocios
        </Link>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">{deal.title}</h1>
            <p className="text-sm">
              <Link to={`/contactos/${deal.contact.id}`} className="text-primary hover:underline">
                {deal.contact.name}
              </Link>{' '}
              · {DEAL_OPERATIONS[deal.operation]}
              {deal.estimatedAmount !== null && (
                <span className="font-semibold tabular-nums">
                  {' '}
                  · {formatMoney(deal.estimatedAmount)}
                </span>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              <DealStatusBadge status={deal.status} />
              {deal.stale && <StaleBadge />}
              <span>
                {deal.pipeline.name} · {deal.stage.name} ({deal.stage.probability} %)
              </span>
              <span>· {deal.assignedTo.name}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              <NextActivity deal={deal} tz={tz} />
            </div>
          </div>
          {deal.can.edit && (
            <div className="flex flex-wrap items-center gap-2">
              {deal.status === 'open' ? (
                <>
                  <Select
                    aria-label="Etapa"
                    className="w-auto"
                    value={deal.stage.id}
                    onChange={(e) =>
                      void act(
                        '/stage',
                        { stageId: e.target.value },
                        { done: 'Etapa actualizada.' },
                      )
                    }
                  >
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="brand"
                    onClick={() => {
                      setDialog('win');
                    }}
                  >
                    Ganado
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDialog('lose');
                    }}
                  >
                    Perdido
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => void act('/reopen', {}, { done: 'Negocio reabierto.' })}
                >
                  Reabrir
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  setDialog('edit');
                }}
              >
                <Pencil /> Editar
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDialog('reassign');
                }}
              >
                <UserRoundCog /> Reasignar
              </Button>
              {deal.can.delete && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDialog('delete');
                  }}
                >
                  <Trash2 /> Eliminar
                </Button>
              )}
            </div>
          )}
        </div>
        {deal.status === 'lost' && deal.lostReason && (
          <Alert tone="info" title={`Perdido: ${deal.lostReason.name}`}>
            {deal.lostComment ?? `Cerrado el ${formatDate(deal.closedAt ?? deal.updatedAt, tz)}.`}
          </Alert>
        )}
        {deal.status === 'won' && (
          <Alert tone="success" title="Ganado">
            Cerrado el {formatDate(deal.closedAt ?? deal.updatedAt, tz)}. Sigue contando en la etapa{' '}
            {deal.stage.name} de {deal.pipeline.name}.
          </Alert>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-5">
          <ActivitiesCard
            link="deal"
            id={deal.id}
            draft={{
              deal: { id: deal.id, label: deal.title },
              contact: { id: deal.contact.id, label: deal.contact.name },
            }}
          />
          <PropertiesCard deal={deal} />
          <TimelineCard deal={deal} />
        </div>
        <div className="space-y-5">
          {deal.operation !== 'listing' && <InterestCard deal={deal} />}
          <ParticipantsCard deal={deal} />
          <Card className="space-y-3">
            <h2 className="font-semibold">Datos</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Canal</dt>
              <dd>{deal.channel?.name ?? '—'}</dd>
              <dt className="text-muted-foreground">Creado</dt>
              <dd>
                {formatDate(deal.createdAt, tz)}
                {deal.createdBy ? ` · ${deal.createdBy}` : ''}
              </dd>
              <dt className="text-muted-foreground">En la etapa desde</dt>
              <dd>{formatDate(deal.stageEnteredAt, tz)}</dd>
              <dt className="text-muted-foreground">Última actividad</dt>
              <dd>{formatDateTime(deal.lastActivityAt, tz)}</dd>
              {(deal.utm.source ?? deal.utm.campaign) && (
                <>
                  <dt className="text-muted-foreground">Campaña</dt>
                  <dd>
                    {[deal.utm.source, deal.utm.medium, deal.utm.campaign]
                      .filter(Boolean)
                      .join(' · ')}
                  </dd>
                </>
              )}
            </dl>
          </Card>
        </div>
      </div>

      {dialog === 'win' && <WinDialog deal={deal} onClose={close} />}
      {dialog === 'lose' && <LoseDialog deal={deal} onClose={close} />}
      {dialog === 'reassign' && <ReassignDialog deal={deal} onClose={close} />}
      {dialog === 'edit' && <EditDialog deal={deal} onClose={close} />}
      {dialog === 'delete' && <DeleteDialog deal={deal} onClose={close} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline and notes with mentions
// ---------------------------------------------------------------------------

function eventText(item: Extract<TimelineItem, { type: 'event' }>): string {
  const d = item.data as Record<string, string | null | undefined>;
  const status = (value: string | null | undefined) =>
    value && value in DEAL_PROPERTY_STATUSES
      ? DEAL_PROPERTY_STATUSES[value as DealPropertyStatus].toLowerCase()
      : '—';
  switch (item.kind) {
    case 'created':
      return 'Creó el negocio';
    case 'stage_changed':
      return `Movió de ${d.fromStageName ?? '—'} a ${d.toStageName ?? '—'}`;
    case 'pipeline_changed':
      return `Cambió de embudo: ${d.fromPipelineName ?? '—'} → ${d.toPipelineName ?? '—'}`;
    case 'won':
      return 'Marcó el negocio como ganado';
    case 'lost':
      return `Marcó el negocio como perdido: ${d.reasonName ?? '—'}`;
    case 'reopened':
      return 'Reabrió el negocio';
    case 'reassigned':
      return `Reasignó de ${d.fromName ?? '—'} a ${d.toName ?? '—'}${
        d.reason === 'deactivation'
          ? ' (al desactivar su acceso)'
          : d.reason === 'no_response'
            ? ' (no respondió el lead a tiempo)'
            : d.reason === 'escalated'
              ? ' (nadie respondió el lead: pasó al encargado de leads)'
              : ''
      }`;
    case 'property_added':
      return `Agregó la propiedad ${d.code ?? ''}`;
    case 'property_status_changed':
      return `Propiedad ${d.code ?? ''}: de ${status(d.from)} a ${status(d.to)}`;
    case 'property_removed':
      return `Quitó la propiedad ${d.code ?? ''}`;
    case 'participant_added':
      return `Agregó a ${d.contactName ?? '—'} como ${
        d.role && d.role in PARTICIPANT_ROLES
          ? PARTICIPANT_ROLES[d.role as ParticipantRole].toLowerCase()
          : '—'
      }`;
    case 'participant_removed':
      return `Quitó a ${d.contactName ?? '—'} de los participantes`;
    case 'lead_received':
      return `Llegó un lead por ${
        d.channel && d.channel in LEAD_CHANNELS
          ? LEAD_CHANNELS[d.channel as keyof typeof LEAD_CHANNELS]
          : (d.channel ?? '—')
      }${d.form ? ` (formulario «${d.form}»)` : ''}`;
    case 'activity_created':
      return `Agendó ${d.typeName?.toLowerCase() ?? 'una actividad'}: ${d.title ?? '—'}`;
    case 'activity_completed':
      return `Completó ${d.typeName?.toLowerCase() ?? 'una actividad'}: ${d.title ?? '—'}${
        d.resultName ? ` (${d.resultName})` : ''
      }${d.comment ? ` · ${d.comment}` : ''}`;
    default:
      return item.kind;
  }
}

function TimelineCard({ deal }: { deal: DealDetail }) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const [kind, setKind] = useState<TimelineKind | undefined>(undefined);
  const timeline = useTimeline(deal.id, kind);
  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Historial</h2>
      {deal.can.edit && <NoteComposer deal={deal} />}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar historial">
        {([undefined, ...(Object.keys(TIMELINE_KINDS) as TimelineKind[])] as const).map((k) => (
          <Button
            key={k ?? 'all'}
            size="sm"
            variant={kind === k ? 'secondary' : 'ghost'}
            aria-pressed={kind === k}
            onClick={() => {
              setKind(k);
            }}
          >
            {k ? TIMELINE_KINDS[k] : 'Todo'}
          </Button>
        ))}
      </div>
      {timeline.isPending ? (
        <Loading />
      ) : timeline.data?.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nada todavía.</p>
      ) : (
        <ol className="space-y-3">
          {timeline.data?.map((item) => (
            <li key={item.id} className="border-l-2 border-border pl-3 text-sm">
              <p className="text-xs text-muted-foreground">
                {formatDateTime(item.at, tz)}
                {item.by ? ` · ${item.by}` : ''}
              </p>
              {item.type === 'note' ? (
                <div className="mt-1 rounded-lg bg-muted p-2.5">
                  <p className="whitespace-pre-line">{item.body}</p>
                  {item.mentions.length > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Mencionó a {item.mentions.map((m) => m.name).join(', ')}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <p>{eventText(item)}</p>
                  {item.kind === 'lead_received' && <LeadDetails data={item.data} />}
                </>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

interface LeadData {
  message: string | null;
  owner: Record<string, string | null> | null;
  page: string | null;
  utmSource: string | null;
  utmCampaign: string | null;
  kept: boolean;
}

const OWNER_FIELDS: Record<string, string> = {
  operation: 'Quiere',
  propertyType: 'Tipo',
  department: 'Departamento',
  location: 'Ubicación',
  expectedPrice: 'Precio esperado',
};

const OWNER_OPERATIONS: Record<string, string> = { sale: 'Vender', rent: 'Alquilar' };

/** What the visitor wrote (kept 90 days), the page and the campaign. */
function LeadDetails({ data }: { data: Record<string, unknown> }) {
  const lead = data.lead as LeadData | undefined;
  if (!lead) return null;
  const owner = Object.entries(lead.owner ?? {}).filter(
    (entry): entry is [string, string] => entry[0] in OWNER_FIELDS && Boolean(entry[1]),
  );
  const campaign = [lead.utmSource, lead.utmCampaign].filter(Boolean).join(' · ');
  return (
    <div className="mt-1 space-y-1 rounded-lg bg-muted p-2.5">
      {!lead.kept ? (
        <p className="text-xs text-muted-foreground">
          El mensaje ya no se conserva (se borra a los 90 días).
        </p>
      ) : (
        <>
          {lead.message && <p className="whitespace-pre-line">{lead.message}</p>}
          {owner.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-xs">
              {owner.map(([key, value]) => (
                <Fragment key={key}>
                  <dt className="text-muted-foreground">{OWNER_FIELDS[key]}</dt>
                  <dd>{key === 'operation' ? (OWNER_OPERATIONS[value] ?? value) : value}</dd>
                </Fragment>
              ))}
            </dl>
          )}
        </>
      )}
      {(lead.page ?? campaign) && (
        <p className="text-xs text-muted-foreground">
          {[lead.page && `Página ${lead.page}`, campaign && `Campaña ${campaign}`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

/**
 * A note, optionally mentioning colleagues. Mentioning gives no access: when someone
 * who cannot see the deal is chosen, the writer is told right away (to share or
 * reassign it), and again after posting.
 */
function NoteComposer({ deal }: { deal: DealDetail }) {
  const client = useQueryClient();
  const notify = useToast();
  const mentionable = useMentionable(deal.id);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<
    { membershipId: string; name: string; canSee: boolean }[]
  >([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const blind = mentions.filter((m) => !m.canSee);
  const options = (mentionable.data ?? [])
    .filter((m) => !mentions.some((x) => x.membershipId === m.membershipId))
    .filter((m) => m.name.toLowerCase().includes(query.trim().toLowerCase()))
    .map((m) => ({
      id: m.membershipId,
      label: m.name,
      ...(m.canSee ? {} : { detail: 'No puede ver este negocio' }),
    }));

  const post = async () => {
    setBusy(true);
    try {
      const result = await api<{ unreachable: { name: string }[] }>(`/deals/${deal.id}/notes`, {
        method: 'POST',
        body: { body, mentions: mentions.map((m) => m.membershipId) },
      });
      setBody('');
      setMentions([]);
      await client.invalidateQueries({ queryKey: ['deals', 'timeline', deal.id] });
      await client.invalidateQueries({ queryKey: dealKeys.detail(deal.id) });
      notify(
        result.unreachable.length
          ? `Nota guardada. ${result.unreachable.map((u) => u.name).join(', ')} no puede ver este negocio.`
          : 'Nota guardada.',
        result.unreachable.length ? 'error' : 'success',
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        void post();
      }}
    >
      <Field label="Nota">
        {(a11y) => (
          <textarea
            {...a11y}
            rows={3}
            maxLength={5000}
            className={textarea}
            placeholder="Qué pasó, qué sigue…"
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
          />
        )}
      </Field>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field label="Mencionar a un colega (opcional)">
            {(a11y) => (
              <Combobox
                id={a11y.id}
                query={query}
                onQueryChange={setQuery}
                options={options}
                onSelect={(option) => {
                  const member = mentionable.data?.find((m) => m.membershipId === option.id);
                  if (member) setMentions((current) => [...current, member]);
                  setQuery('');
                }}
                placeholder="@nombre"
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="brand" loading={busy} disabled={!body.trim()}>
          Guardar nota
        </Button>
      </div>
      {mentions.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {mentions.map((m) => (
            <li
              key={m.membershipId}
              className="flex items-center gap-1 rounded-full bg-soft py-0.5 pr-0.5 pl-2 text-sm text-soft-foreground"
            >
              <AtSign className="size-3.5" aria-hidden="true" />
              {m.name}
              <button
                type="button"
                className="flex size-6 items-center justify-center rounded-full hover:bg-surface"
                aria-label={`Quitar mención de ${m.name}`}
                onClick={() => {
                  setMentions((current) =>
                    current.filter((x) => x.membershipId !== m.membershipId),
                  );
                }}
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {blind.length > 0 && (
        <Alert tone="warning" title="La mención no da acceso">
          {blind.map((m) => m.name).join(', ')} no {blind.length === 1 ? 'puede' : 'pueden'} ver
          este negocio: recibirá el aviso pero no podrá abrirlo. Si necesita verlo, reasígnaselo o
          pide a quien corresponda que se lo comparta.
        </Alert>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Properties, suggestions and participants
// ---------------------------------------------------------------------------

function PropertiesCard({ deal }: { deal: DealDetail }) {
  const act = useDealAction(deal.id);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestions = useSuggestions(deal.id, showSuggestions && deal.can.edit);
  const [query, setQuery] = useState('');
  const search = useProperties({ q: query.trim() }, query.trim().length > 0);
  const price = (p: { salePrice: number | null; rentPrice: number | null }) =>
    priceLabel({ operation: 'sale_rent', ...p }, (n) => formatMoney(n));
  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Propiedades</h2>
        {deal.can.edit && deal.operation !== 'listing' && (
          <Button
            size="sm"
            variant="secondary"
            aria-expanded={showSuggestions}
            onClick={() => {
              setShowSuggestions((v) => !v);
            }}
          >
            {showSuggestions ? 'Ocultar sugerencias' : 'Ver sugerencias'}
          </Button>
        )}
      </div>
      {deal.properties.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no hay propiedades en este negocio.</p>
      ) : (
        <ul className="divide-y divide-border">
          {deal.properties.map((l) => (
            <li key={l.linkId} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
              <Thumb url={l.property.coverUrl} />
              <div className="min-w-0 flex-1">
                {l.property.visible ? (
                  <Link
                    to={`/propiedades/${l.property.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {l.property.title}
                  </Link>
                ) : (
                  <span className="font-medium">{l.property.title}</span>
                )}
                <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="font-mono">{l.property.code}</span>
                  <StatusBadge status={l.property.status} />
                  <span>
                    {l.property.district} · {price(l.property)}
                  </span>
                </p>
              </div>
              {deal.can.edit ? (
                <div className="flex items-center gap-1">
                  <Select
                    aria-label={`Estado de ${l.property.code}`}
                    className="min-h-9 w-auto text-sm"
                    value={l.status}
                    onChange={(e) =>
                      void act(
                        `/properties/${l.linkId}`,
                        { status: e.target.value },
                        { method: 'PATCH' },
                      )
                    }
                  >
                    {(Object.entries(DEAL_PROPERTY_STATUSES) as [DealPropertyStatus, string][]).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Quitar ${l.property.code}`}
                    onClick={() =>
                      void act(`/properties/${l.linkId}`, undefined, { method: 'DELETE' })
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              ) : (
                <Badge>{DEAL_PROPERTY_STATUSES[l.status]}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      {deal.can.edit && (
        <Field label="Agregar una propiedad" hint="Busca por código, título o zona.">
          {(a11y) => (
            <Combobox
              id={a11y.id}
              describedBy={a11y['aria-describedby']}
              query={query}
              onQueryChange={setQuery}
              loading={search.isFetching}
              options={(query.trim() ? (search.data?.items ?? []) : [])
                .filter((p) => !deal.properties.some((l) => l.property.id === p.id))
                .map((p) => ({
                  id: p.id,
                  label: p.title,
                  detail: `${p.code} · ${p.district.name}`,
                }))}
              onSelect={(option) => {
                setQuery('');
                void act('/properties', { propertyId: option.id }, { done: 'Propiedad agregada.' });
              }}
              placeholder="P7K3M9Q, Santa Tecla…"
            />
          )}
        </Field>
      )}
      {showSuggestions && (
        <section className="space-y-2 rounded-lg border border-border p-3" aria-label="Sugerencias">
          <h3 className="text-sm font-medium">Sugerencias según lo que busca</h3>
          {suggestions.isPending ? (
            <Loading />
          ) : suggestions.data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ninguna propiedad disponible encaja. Revisa el presupuesto o las zonas.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {suggestions.data?.map((s) => (
                <li key={s.id} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
                  <Thumb url={s.coverUrl} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/propiedades/${s.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {s.title}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {PROPERTY_TYPES[s.type].label} · {s.district} · {price(s)}
                      {s.missing.length > 0 ? ` · no cumple: ${s.missing.join(', ')}` : ''}
                    </p>
                  </div>
                  <Badge tone={s.match === 100 ? 'success' : 'info'}>{s.match} %</Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void act('/properties', { propertyId: s.id }, { done: 'Propiedad agregada.' })
                    }
                  >
                    <Plus /> Agregar
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </Card>
  );
}

function Thumb({ url }: { url: string | null }) {
  return (
    <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
      {url && <img src={url} alt="" className="size-full object-cover" loading="lazy" />}
    </div>
  );
}

function InterestCard({ deal }: { deal: DealDetail }) {
  const i = deal.interest;
  const rows: [string, ReactNode][] = [
    ['Tipo', i.propertyTypes.map((t) => PROPERTY_TYPES[t].label).join(', ') || 'Cualquiera'],
    ['Zonas', i.districts.map((d) => d.name).join(', ') || 'Cualquiera'],
    [
      'Presupuesto',
      i.budgetMin === null && i.budgetMax === null
        ? '—'
        : [
            i.budgetMin !== null && `desde ${formatMoney(i.budgetMin)}`,
            i.budgetMax !== null && `hasta ${formatMoney(i.budgetMax)}`,
          ]
            .filter(Boolean)
            .join(' '),
    ],
    [
      'Mínimos',
      [
        i.bedroomsMin !== null && `${String(i.bedroomsMin)} hab.`,
        i.bathroomsMin !== null && `${String(i.bathroomsMin)} baños`,
        i.parkingMin !== null && `${String(i.parkingMin)} parqueos`,
      ]
        .filter(Boolean)
        .join(' · ') || '—',
    ],
  ];
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Qué busca</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function ParticipantsCard({ deal }: { deal: DealDetail }) {
  const act = useDealAction(deal.id);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<ParticipantRole>('co_buyer');
  const results = useContacts(query.trim() ? { q: query.trim() } : {}, query.trim().length > 0);
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Participantes</h2>
      {deal.participants.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin co-compradores ni referidores.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {deal.participants.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span>
                <Link to={`/contactos/${p.contact.id}`} className="text-primary hover:underline">
                  {p.contact.name}
                </Link>{' '}
                · {PARTICIPANT_ROLES[p.role]}
              </span>
              {deal.can.edit && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Quitar a ${p.contact.name}`}
                  onClick={() => void act(`/participants/${p.id}`, undefined, { method: 'DELETE' })}
                >
                  <Trash2 />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {deal.can.edit && (
        <div className="grid gap-2 sm:grid-cols-[1fr_9rem]">
          <Field label="Agregar participante">
            {(a11y) => (
              <Combobox
                id={a11y.id}
                query={query}
                onQueryChange={setQuery}
                loading={results.isFetching}
                options={(results.data?.items ?? [])
                  .filter((c) => c.id !== deal.contact.id)
                  .map((c) => ({
                    id: c.id,
                    label: c.name,
                    detail: [c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · '),
                  }))}
                onSelect={(option) => {
                  setQuery('');
                  void act('/participants', { contactId: option.id, role });
                }}
                placeholder="Nombre o teléfono"
              />
            )}
          </Field>
          <Field label="Papel">
            {(a11y) => (
              <Select
                {...a11y}
                value={role}
                onChange={(e) => {
                  setRole(e.target.value as ParticipantRole);
                }}
              >
                {(Object.entries(PARTICIPANT_ROLES) as [ParticipantRole, string][]).map(
                  ([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ),
                )}
              </Select>
            )}
          </Field>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function DialogButtons({
  busy,
  label,
  onClose,
  variant = 'brand',
  disabled = false,
}: {
  busy: boolean;
  label: string;
  onClose: () => void;
  variant?: 'brand' | 'danger';
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button variant="ghost" onClick={onClose}>
        Cancelar
      </Button>
      <Button type="submit" variant={variant} loading={busy} disabled={disabled}>
        {label}
      </Button>
    </div>
  );
}

function WinDialog({ deal, onClose }: { deal: DealDetail; onClose: () => void }) {
  const act = useDealAction(deal.id);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open
      title="Marcar como ganado"
      description={`Queda como ganado en la etapa ${deal.stage.name}. Cuando esté el módulo de cierres, desde aquí se registrará la reserva.`}
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void act('/win', { note: note.trim() || undefined }, { done: '¡Negocio ganado!' }).then(
            (ok) => {
              if (ok) onClose();
              else setBusy(false);
            },
          );
        }}
      >
        <Field label="Nota (opcional)" hint="Por ejemplo: reserva firmada, forma de pago.">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              className={textarea}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
        </Field>
        <DialogButtons busy={busy} label="Marcar como ganado" onClose={onClose} />
      </form>
    </Dialog>
  );
}

function LoseDialog({ deal, onClose }: { deal: DealDetail; onClose: () => void }) {
  const act = useDealAction(deal.id);
  const reasons = useLostReasons();
  const [reasonId, setReasonId] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open title="Marcar como perdido" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void act(
            '/lose',
            { reasonId, comment: comment.trim() || undefined },
            { done: 'Negocio marcado como perdido.' },
          ).then((ok) => {
            if (ok) onClose();
            else setBusy(false);
          });
        }}
      >
        <Field label="Motivo">
          {(a11y) => (
            <Select
              {...a11y}
              value={reasonId}
              onChange={(e) => {
                setReasonId(e.target.value);
              }}
            >
              <option value="">Elige el motivo</option>
              {(reasons.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Comentario (opcional)">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              className={textarea}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
            />
          )}
        </Field>
        <DialogButtons
          busy={busy}
          label="Marcar como perdido"
          onClose={onClose}
          disabled={!reasonId}
        />
      </form>
    </Dialog>
  );
}

function ReassignDialog({ deal, onClose }: { deal: DealDetail; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const assignees = useAssignees();
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api('/deals/reassign', {
        method: 'POST',
        body: { dealIds: [deal.id], toMembershipId: to, reason },
      });
      await client.invalidateQueries({ queryKey: dealKeys.all });
      notify('Negocio reasignado.', 'success');
      onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title="Reasignar negocio"
      description="La nueva persona recibe un aviso; el motivo queda en el historial."
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Pasa a">
          {(a11y) => (
            <Select
              {...a11y}
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            >
              <option value="">Elige a alguien</option>
              {(assignees.data ?? [])
                .filter((m) => m.membershipId !== deal.assignedTo.membershipId)
                .map((m) => (
                  <option key={m.membershipId} value={m.membershipId}>
                    {m.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label="Motivo">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={300}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          )}
        </Field>
        <DialogButtons
          busy={busy}
          label="Reasignar"
          onClose={onClose}
          disabled={!to || reason.trim().length < 3}
        />
      </form>
    </Dialog>
  );
}

function EditDialog({ deal, onClose }: { deal: DealDetail; onClose: () => void }) {
  const act = useDealAction(deal.id);
  const channels = useChannels();
  const [title, setTitle] = useState(deal.title);
  const [amount, setAmount] = useState(
    deal.estimatedAmount === null ? '' : String(deal.estimatedAmount),
  );
  const [channelId, setChannelId] = useState(deal.channel?.id ?? '');
  const str = (n: number | null) => (n === null ? '' : String(n));
  const [interest, setInterest] = useState<InterestValue>({
    propertyTypes: deal.interest.propertyTypes,
    districts: deal.interest.districts.map((d) => ({ id: d.id, name: d.name })),
    budgetMin: str(deal.interest.budgetMin),
    budgetMax: str(deal.interest.budgetMax),
    bedroomsMin: str(deal.interest.bedroomsMin),
    bathroomsMin: str(deal.interest.bathroomsMin),
    parkingMin: str(deal.interest.parkingMin),
  });
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open
      title="Editar negocio"
      onClose={onClose}
      className="sm:w-[min(44rem,calc(100vw-2rem))]"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void act(
            '',
            {
              title,
              estimatedAmount: amount.trim() ? Number(amount) : null,
              channelId: channelId || null,
              interest: interestBody(interest),
            },
            { method: 'PATCH', done: 'Cambios guardados.' },
          ).then((ok) => {
            if (ok) onClose();
            else setBusy(false);
          });
        }}
      >
        <Field label="Título">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={150}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Monto estimado (USD)">
            {(a11y) => (
              <Input
                {...a11y}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label="Canal">
            {(a11y) => (
              <Select
                {...a11y}
                value={channelId}
                onChange={(e) => {
                  setChannelId(e.target.value);
                }}
              >
                <option value="">Sin canal</option>
                {(channels.data ?? [])
                  .filter((c) => c.active || c.id === deal.channel?.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        </div>
        <h3 className="text-sm font-semibold">Qué busca</h3>
        <InterestFields value={interest} onChange={setInterest} />
        <DialogButtons
          busy={busy}
          label="Guardar cambios"
          onClose={onClose}
          disabled={!title.trim()}
        />
      </form>
    </Dialog>
  );
}

function DeleteDialog({ deal, onClose }: { deal: DealDetail; onClose: () => void }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open
      title="Eliminar negocio"
      description="Va a la papelera: se puede restaurar durante 30 días."
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void api(`/deals/${deal.id}`, { method: 'DELETE' })
            .then(async () => {
              await client.invalidateQueries({ queryKey: dealKeys.all });
              notify(`«${deal.title}» se envió a la papelera.`, 'success');
              await navigate('/negocios');
            })
            .catch((error: unknown) => {
              notify(errorMessage(error), 'error');
              setBusy(false);
            });
        }}
      >
        <div className="flex items-start gap-2 pb-4 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          Sus notas e historial se van con él.
        </div>
        <DialogButtons
          busy={busy}
          label="Enviar a la papelera"
          onClose={onClose}
          variant="danger"
        />
      </form>
    </Dialog>
  );
}
